import axios from "../plugins/axios";
import config from "../../app.config";
import { defineStore } from "pinia";
import Vue from "vue";

export const useVideoBatchStore = defineStore("videoBatch", {
  state: () => {
    return {
      batches: {},
      batchList: [],
      presets: [],
      pluginCatalog: [],
      isLoading: false,
      isUploading: false,
      isExportingElan: false,
      elanExportProgress: 0,
      elanExportProcessed: 0,
      elanExportTotal: 0,
      elanExportExported: 0,
      elanExportFailed: 0,
      elanExportMessage: "",
      progress: 0,
    };
  },
  getters: {
    all(state) {
      return state.batchList.map((id) => state.batches[id]);
    },
    get(state) {
      return (id) => state.batches[id];
    },
  },
  actions: {
    async fetchPresets() {
      return axios.get(`${config.API_LOCATION}/video/batch/presets`).then((res) => {
        if (res.data.status === "ok") {
          this.presets = res.data.entries;
        }
      });
    },
    async savePreset({ id = null, name, description = "", steps }) {
      return axios
        .post(`${config.API_LOCATION}/video/batch/presets/save`, {
          id,
          name,
          description,
          steps,
        })
        .then((res) => {
          if (res.data.status === "ok") {
            const entry = res.data.entry;
            const index = this.presets.findIndex((preset) => preset.id === entry.id);
            if (index === -1) {
              this.presets.push(entry);
            } else {
              this.presets.splice(index, 1, entry);
            }
          }
          return res.data;
        });
    },
    async deletePreset(presetId) {
      return axios
        .post(`${config.API_LOCATION}/video/batch/presets/delete`, {
          id: presetId,
        })
        .then((res) => {
          if (res.data.status === "ok") {
            this.presets = this.presets.filter((preset) => preset.id !== presetId);
          }
          return res.data;
        });
    },
    async fetchPluginCatalog() {
      return axios.get(`${config.API_LOCATION}/video/batch/plugin-catalog`).then((res) => {
        if (res.data.status === "ok") {
          this.pluginCatalog = res.data.entries;
        }
      });
    },
    async fetchAll() {
      if (this.isLoading) return;
      this.isLoading = true;
      return axios
        .get(`${config.API_LOCATION}/video/batch/list`)
        .then((res) => {
          if (res.data.status === "ok") {
            const batches = {};
            const batchList = [];
            res.data.entries.forEach((batch) => {
              batches[batch.id] = { ...this.batches[batch.id], ...batch };
              batchList.push(batch.id);
            });
            this.batches = batches;
            this.batchList = batchList;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async fetch(batchId, { summary = false } = {}) {
      this.isLoading = true;
      return axios
        .get(`${config.API_LOCATION}/video/batch/get`, {
          params: { id: batchId, summary },
        })
        .then((res) => {
          if (res.data.status === "ok") {
            const entry = res.data.entry;
            Vue.set(this.batches, entry.id, entry);
            if (!this.batchList.includes(res.data.entry.id)) {
              this.batchList.push(res.data.entry.id);
            }
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async fetchItems(batchId, params) {
      const response = await axios.get(`${config.API_LOCATION}/video/batch/items`, {
        params: { id: batchId, ...params },
      });
      return response.data;
    },
    async fetchItemIds(batchId, params) {
      const response = await axios.get(`${config.API_LOCATION}/video/batch/items/ids`, {
        params: { id: batchId, ...params },
      });
      return response.data.entries;
    },
    async uploadSharedInput({ batchId, file }) {
      const formData = new FormData();
      formData.append("id", batchId);
      formData.append("file", file);
      return axios
        .post(`${config.API_LOCATION}/video/batch/shared-input/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => res.data);
    },
    async upload({ mode, files = [], zip = null, name, preset, autoRunPreset }) {
      const formData = new FormData();
      formData.append("name", name || "");
      if (preset) {
        formData.append("preset", preset);
      }
      formData.append("auto_run_preset", autoRunPreset ? "true" : "false");

      if (mode === "zip") {
        formData.append("zip", zip);
      } else {
        files.forEach((file) => {
          formData.append("files", file);
        });
        formData.append(
          "paths",
          JSON.stringify(files.map((file) => file.webkitRelativePath || file.name))
        );
      }

      this.isUploading = true;
      return axios
        .post(`${config.API_LOCATION}/video/batch/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => {
            if (event.total) {
              this.progress = Math.round((event.loaded * 100) / event.total);
            }
          },
        })
        .then((res) => res.data)
        .finally(() => {
          this.isUploading = false;
          this.progress = 0;
        });
    },
    async runPreset({ batchId, preset = null }) {
      return axios.post(`${config.API_LOCATION}/video/batch/run-preset`, {
        id: batchId,
        preset,
      });
    },
    async runScopedPreset({ batchId, preset = null, scope = null }) {
      return axios.post(`${config.API_LOCATION}/video/batch/run-preset`, {
        id: batchId,
        preset,
        scope,
      });
    },
    async runPluginSet({ batchId, name, scope, steps }) {
      return axios.post(`${config.API_LOCATION}/video/batch/run-plugin-set`, {
        id: batchId,
        name,
        scope,
        steps,
      });
    },
    async validatePluginSet({ batchId, name, scope, steps }) {
      return axios.post(`${config.API_LOCATION}/video/batch/validate-plugin-set`, {
        id: batchId,
        name,
        scope,
        steps,
      });
    },
    async retryFailed(batchId) {
      return axios.post(`${config.API_LOCATION}/video/batch/retry-failed`, {
        id: batchId,
      });
    },
    async retryFailedPluginSteps(batchId) {
      return axios.post(
        `${config.API_LOCATION}/video/batch/retry-failed-plugin-steps`,
        { id: batchId }
      );
    },
    async exportElan(batchId, batchName, applyFiltering = true) {
      this.isExportingElan = true;
      this.elanExportProgress = 0;
      this.elanExportProcessed = 0;
      this.elanExportTotal = 0;
      this.elanExportExported = 0;
      this.elanExportFailed = 0;
      this.elanExportMessage = "Starting export…";

      try {
        const start = await axios.post(`${config.API_LOCATION}/video/batch/export-elan`, {
          id: batchId,
          async: true,
          apply_filtering: applyFiltering,
        });
        const jobId = start.data.job_id;
        this.elanExportTotal = start.data.total || 0;

        while (true) {
          const status = await axios.get(
            `${config.API_LOCATION}/video/batch/export-elan/status`,
            { params: { id: jobId } }
          );
          const data = status.data;
          this.elanExportProgress = data.progress || 0;
          this.elanExportProcessed = data.processed || 0;
          this.elanExportTotal = data.total || this.elanExportTotal;
          this.elanExportExported = data.exported || 0;
          this.elanExportFailed = data.failed || 0;
          this.elanExportMessage =
            data.phase === "queued"
              ? "Waiting for an export worker…"
              : data.phase === "finalizing"
                ? "Finalizing archive…"
                : `Exporting files (${this.elanExportProcessed}/${this.elanExportTotal})…`;

          if (data.status === "complete") {
            this.elanExportMessage = "Preparing download…";
            const archive = await axios.get(
              `${config.API_LOCATION}/video/batch/export-elan/download`,
              { params: { id: jobId }, responseType: "blob" }
            );
            const disposition = archive.headers["content-disposition"] || "";
            const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
            const exportBaseName = (batchName || batchId).slice(0, 64);
            const filename = filenameMatch
              ? filenameMatch[1]
              : `${exportBaseName}-${applyFiltering ? "elan" : "raw-elan"}.zip`;
            const url = URL.createObjectURL(
              new Blob([archive.data], { type: "application/zip" })
            );
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            return;
          }
          if (data.status === "error") {
            throw new Error(data.error || "ELAN export failed");
          }
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } finally {
        this.isExportingElan = false;
        this.elanExportMessage = "";
      }
    },
    async delete(batchId) {
      return axios
        .post(`${config.API_LOCATION}/video/batch/delete`, { id: batchId })
        .then((res) => {
          if (res.data.status === "ok") {
            this.batchList = this.batchList.filter((id) => id !== batchId);
            Vue.delete(this.batches, batchId);
          }
        });
    },
    async cancel(batchId) {
      return axios.post(`${config.API_LOCATION}/video/batch/cancel`, { id: batchId });
    },
  },
});
